/**
 * CaseMakerPage
 *
 * Main CaseMaker dashboard showing generated documents list and quick access to create new documents.
 * Follows the layout pattern from other pages (sidebar + main content).
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCaseFile } from '../contexts/CaseFileContext';
import { Sidebar, Header } from '../components/dashboard';
import {
  ArrowLeft,
  Plus,
  FileText,
  ClipboardList,
  Filter,
  Search,
  ChevronRight,
} from 'lucide-react';
import { casemakerService } from '../services/casemakerService';
import {
  GeneratedDocumentSummary,
  DocumentStatus,
  DocumentGenerationType,
  DOCUMENT_TYPES,
  STATUS_STYLES,
  CATEGORY_STYLES,
  getDocumentTypeInfo,
  formatTimestamp,
  formatRelativeTime,
} from '../types/generatedDoc';

export function CaseMakerPage(): JSX.Element {
  const { caseFileId: urlCaseFileId } = useParams<{ caseFileId: string }>();
  const navigate = useNavigate();

  // Use CaseFile context
  const {
    caseFiles,
    activeCaseFileId,
    setActiveCaseFileId,
    refreshCaseFiles,
  } = useCaseFile();

  // Sync URL caseFileId with context
  const caseFileId = urlCaseFileId || activeCaseFileId;

  // Update context if URL has a different caseFileId
  useEffect(() => {
    if (urlCaseFileId && urlCaseFileId !== activeCaseFileId) {
      setActiveCaseFileId(urlCaseFileId);
    }
  }, [urlCaseFileId, activeCaseFileId, setActiveCaseFileId]);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Data state
  const [documents, setDocuments] = useState<GeneratedDocumentSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<DocumentGenerationType | ''>('');
  const [searchQuery, setSearchQuery] = useState('');

  const hasFilters = statusFilter || typeFilter || searchQuery;

  const loadDocuments = useCallback(async () => {
    if (!caseFileId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await casemakerService.listDocuments({
        caseFileId,
        status: statusFilter || undefined,
        documentType: typeFilter || undefined,
        limit: 50,
      });

      if (response.success) {
        setDocuments(response.documents);
        setTotalCount(response.totalCount);
      } else {
        setError(response.error || 'Failed to load documents');
      }
    } catch (err) {
      console.error('Error loading CaseMaker documents:', err);
      const rawMessage = err instanceof Error ? err.message : '';
      if (rawMessage.toLowerCase().includes('not found') || rawMessage.toLowerCase().includes('does not have access')) {
        setError('This care record could not be found. It may have been removed or you may not have access.');
      } else {
        setError('Something went wrong while loading documents. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [caseFileId, statusFilter, typeFilter]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleBackToCaseFile = () => {
    navigate(`/casefile/${caseFileId}`);
  };

  const handleCreateNew = () => {
    navigate(`/casefile/${caseFileId}/casemaker/new`);
  };

  const handleFillForm = () => {
    navigate(`/casefile/${caseFileId}/casemaker/form-filler`);
  };

  const handleDocumentClick = (docId: string) => {
    navigate(`/casefile/${caseFileId}/casemaker/${docId}`);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setSearchQuery('');
  };

  const handleCaseFileChange = (newCaseFileId: string) => {
    setActiveCaseFileId(newCaseFileId);
    navigate(`/casefile/${newCaseFileId}/casemaker`);
  };

  const handleJoinedCareTeam = (newCaseFileId: string) => {
    setActiveCaseFileId(newCaseFileId);
    refreshCaseFiles();
    navigate(`/casefile/${newCaseFileId}/casemaker`);
  };

  // Filter documents by search query (client-side)
  const filteredDocuments = documents.filter((doc) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      doc.title.toLowerCase().includes(query) ||
      getDocumentTypeInfo(doc.documentType)?.name.toLowerCase().includes(query)
    );
  });

  if (!caseFileId) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <div className="card p-8 max-w-md text-center">
          <h2 className="text-lg font-semibold text-ink mb-2">No Care Record Selected</h2>
          <p className="text-muted mb-6">Please select a care record from the dashboard to view documents.</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-airy flex">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
      </div>

      {/* Sidebar - Mobile Overlay */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-ink/40 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar className="h-full" activeCaseFileId={caseFileId} />
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Global Header with CaseFile Selector */}
        <Header
          caseFiles={caseFiles.map((cf) => ({ id: cf.caseFileId, name: cf.name }))}
          activeCaseFileId={caseFileId}
          onCaseFileChange={handleCaseFileChange}
          onMenuClick={() => setSidebarOpen(true)}
          showMobileMenu={true}
          onJoinedCareTeam={handleJoinedCareTeam}
        />

        {/* Page Header */}
        <div className="bg-white border-b border-soft-neutral px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBackToCaseFile}
                className="btn-ghost btn-sm p-1 lg:hidden"
                aria-label="Back to CaseFile"
              >
                <ArrowLeft size={20} aria-hidden="true" />
              </button>
              <button
                onClick={handleBackToCaseFile}
                className="hidden lg:flex btn-ghost btn-sm items-center gap-1"
              >
                <ArrowLeft size={16} />
                Back to CaseFile
              </button>
              <div className="h-6 w-px bg-soft-neutral hidden lg:block" />
              <h1 className="text-xl font-semibold text-ink">CaseMaker</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleFillForm}
                className="btn-ghost flex items-center gap-2 border border-soft-neutral"
              >
                <ClipboardList size={18} />
                <span className="hidden sm:inline">Fill a Form</span>
              </button>
              <button
                onClick={handleCreateNew}
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">New Document</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div id="main-content" role="main" tabIndex={-1} className="flex-1 p-4 lg:p-6 outline-none">
          <div className="max-w-4xl mx-auto">
            {/* Stats Bar */}
            <div className="card mb-6">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-2xl font-semibold text-ink">{totalCount}</p>
                      <p className="text-sm text-muted">Total Documents</p>
                    </div>
                    <div className="h-10 w-px bg-soft-neutral" />
                    <div>
                      <p className="text-2xl font-semibold text-evergreen">
                        {documents.filter((d) => d.status === 'ready').length}
                      </p>
                      <p className="text-sm text-muted">Ready</p>
                    </div>
                    <div className="h-10 w-px bg-soft-neutral hidden sm:block" />
                    <div className="hidden sm:block">
                      <p className="text-2xl font-semibold text-teal-600">
                        {documents.filter((d) => d.status === 'submitted').length}
                      </p>
                      <p className="text-sm text-muted">Submitted</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="mb-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={18} />
                  <input
                    type="text"
                    placeholder="Search documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input pl-10 w-full"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`btn-ghost flex items-center gap-2 ${hasFilters ? 'text-evergreen' : ''}`}
                >
                  <Filter size={18} />
                  Filters
                  {hasFilters && (
                    <span className="badge badge-primary badge-sm">
                      {[statusFilter, typeFilter].filter(Boolean).length}
                    </span>
                  )}
                </button>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="card">
                  <div className="card-body">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="label">Status</label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value as DocumentStatus | '')}
                          className="select w-full"
                        >
                          <option value="">All Statuses</option>
                          <option value="draft">Draft</option>
                          <option value="generating">Generating</option>
                          <option value="ready">Ready</option>
                          <option value="exported">Exported</option>
                          <option value="submitted">Submitted</option>
                        </select>
                      </div>
                      <div>
                        <label className="label">Document Type</label>
                        <select
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value as DocumentGenerationType | '')}
                          className="select w-full"
                        >
                          <option value="">All Types</option>
                          {DOCUMENT_TYPES.map((type) => (
                            <option key={type.type} value={type.type}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {hasFilters && (
                      <div className="mt-4 pt-4 border-t border-soft-neutral">
                        <button onClick={clearFilters} className="btn-ghost btn-sm text-muted">
                          Clear All Filters
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Error State */}
            {error && (
              <div className="card border-red-200 bg-red-50 mb-4">
                <div className="card-body">
                  <p className="text-red-700">{error}</p>
                  <button onClick={loadDocuments} className="btn-ghost btn-sm mt-2">
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="card">
                <div className="card-body text-center py-12">
                  <div className="animate-pulse">
                    <FileText className="mx-auto mb-4 text-muted" size={48} />
                    <p className="text-muted">Loading documents...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && filteredDocuments.length === 0 && (
              <div className="card">
                <div className="card-body text-center py-12">
                  <FileText className="mx-auto mb-4 text-muted" size={48} />
                  <h3 className="font-semibold text-ink mb-2">
                    {hasFilters ? 'No documents match your filters' : 'No documents yet'}
                  </h3>
                  <p className="text-muted mb-4">
                    {hasFilters
                      ? 'Try adjusting your filters or search query'
                      : 'Create your first advocacy document to get started'}
                  </p>
                  {hasFilters ? (
                    <button onClick={clearFilters} className="btn-ghost">
                      Clear Filters
                    </button>
                  ) : (
                    <button onClick={handleCreateNew} className="btn-primary">
                      <Plus size={18} className="mr-2" />
                      Create Document
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Documents List */}
            {!isLoading && !error && filteredDocuments.length > 0 && (
              <div className="space-y-3">
                {filteredDocuments.map((doc) => {
                  const typeInfo = getDocumentTypeInfo(doc.documentType);
                  const statusStyle = STATUS_STYLES[doc.status];
                  const categoryStyle = typeInfo ? CATEGORY_STYLES[typeInfo.category] : null;
                  const createdDate = formatTimestamp(doc.createdAt);

                  return (
                    <div
                      key={doc.docId}
                      onClick={() => handleDocumentClick(doc.docId)}
                      className="card hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="card-body">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              {categoryStyle && (
                                <span className={`badge ${categoryStyle.bg} ${categoryStyle.text} badge-sm`}>
                                  {categoryStyle.label}
                                </span>
                              )}
                              <span className={`badge ${statusStyle.bg} ${statusStyle.text} badge-sm`}>
                                {statusStyle.label}
                              </span>
                              {doc.hasBeenEdited && (
                                <span className="badge bg-yellow-100 text-yellow-700 badge-sm">
                                  Edited
                                </span>
                              )}
                            </div>
                            <h3 className="font-semibold text-ink truncate">{doc.title}</h3>
                            <p className="text-sm text-muted">
                              {typeInfo?.name || doc.documentType}
                            </p>
                            <p className="text-xs text-muted mt-1">
                              Created {formatRelativeTime(createdDate)}
                            </p>
                          </div>
                          <ChevronRight className="text-muted flex-shrink-0" size={20} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CaseMakerPage;
